import React from 'react';

/**
 * Consistent padding rules applied to ALL nodes:
 *   tag   y = rect.y + 24
 *   title y = rect.y + 50
 *   d1    y = rect.y + 76
 *   d2    y = rect.y + 94  (+18)
 *   d3    y = rect.y + 112 (+18)
 *   d4    y = rect.y + 130 (+18)
 *   d5    y = rect.y + 148 (+18)
 *   badge y = last detail y + 14  (badge h=18)
 *   left  x = rect.x + 16
 *   shine : rect.x, rect.y, rect.width, h=1
 */

const ArchitectureDiagram: React.FC = () => (
  <div style={{ background: '#0b0f1a', borderRadius: 16, padding: 24 }}>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600&family=Syne:wght@800&display=swap');
      @keyframes fadein { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:translateY(0);} }
      .arch-svg { animation: fadein 0.6s ease both; }
      .arch-node { transition: filter 0.2s ease; cursor: default; }
      .arch-node:hover { filter: brightness(1.15) drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
    `}</style>

    <svg
      className="arch-svg"
      viewBox="0 0 1120 800"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Glow filters */}
        <filter id="gl-ind" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="8" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="gl-grn" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="10" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="gl-sm" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>

        {/* Node gradients */}
        <linearGradient id="g-client"   x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3730a3" stopOpacity=".7"/><stop offset="100%" stopColor="#1e1b4b" stopOpacity=".9"/></linearGradient>
        <linearGradient id="g-server"   x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#065f46" stopOpacity=".8"/><stop offset="100%" stopColor="#022c22" stopOpacity=".95"/></linearGradient>
        <linearGradient id="g-db"       x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7f1d1d" stopOpacity=".8"/><stop offset="100%" stopColor="#450a0a" stopOpacity=".95"/></linearGradient>
        <linearGradient id="g-firebase" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#78350f" stopOpacity=".8"/><stop offset="100%" stopColor="#451a03" stopOpacity=".95"/></linearGradient>
        <linearGradient id="g-gemini"   x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0e4f6e" stopOpacity=".8"/><stop offset="100%" stopColor="#082f43" stopOpacity=".95"/></linearGradient>
        <linearGradient id="g-resend"   x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#1a3a2a" stopOpacity=".8"/><stop offset="100%" stopColor="#0a1f14" stopOpacity=".95"/></linearGradient>
        <linearGradient id="g-railway"  x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#1e2533" stopOpacity=".95"/><stop offset="100%" stopColor="#0d111a" stopOpacity="1"/></linearGradient>
        <linearGradient id="g-docker"   x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0c3554" stopOpacity=".8"/><stop offset="100%" stopColor="#071d31" stopOpacity=".95"/></linearGradient>

        {/* Arrow markers */}
        <marker id="arr"    markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="rgba(148,163,184,0.55)"/></marker>
        <marker id="arr-hi" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="rgba(99,102,241,0.8)"/></marker>
      </defs>

      {/* Ambient glows */}
      <ellipse cx="300" cy="200" rx="220" ry="140" fill="rgba(99,102,241,0.06)"/>
      <ellipse cx="650" cy="190" rx="260" ry="160" fill="rgba(16,185,129,0.06)"/>
      <ellipse cx="720" cy="570" rx="300" ry="130" fill="rgba(6,182,212,0.04)"/>

      {/* ── ARROWS (drawn first, under nodes) ── */}
      {/* Client → Server */}
      <path d="M 340 160 L 470 185" stroke="rgba(99,102,241,0.5)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr-hi)"/>
      <text x="400" y="150" textAnchor="middle" fill="rgba(165,180,252,0.95)" fontSize="11" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".05em">REST API</text>

      {/* Server → MongoDB */}
      <path d="M 530 236 L 255 380" stroke="rgba(148,163,184,0.3)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>
      <text x="355" y="292" textAnchor="middle" fill="rgba(203,213,225,0.85)" fontSize="10" fontFamily="'JetBrains Mono',monospace" fontWeight="600">Mongoose</text>

      {/* Server → Firebase */}
      <path d="M 570 236 L 490 380" stroke="rgba(148,163,184,0.3)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>

      {/* Server → Gemini */}
      <path d="M 655 236 L 705 380" stroke="rgba(6,182,212,0.45)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>
      <text x="704" y="300" textAnchor="middle" fill="rgba(103,232,249,0.9)" fontSize="10" fontFamily="'JetBrains Mono',monospace" fontWeight="600">AI Calls</text>

      {/* Server → Resend */}
      <path d="M 745 236 L 940 380" stroke="rgba(148,163,184,0.3)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>

      {/* Server → Railway */}
      <path d="M 635 236 L 598 560" stroke="rgba(148,163,184,0.25)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>
      <text x="670" y="430" textAnchor="start" fill="rgba(203,213,225,0.8)" fontSize="10" fontFamily="'JetBrains Mono',monospace" fontWeight="600">Deploy</text>

      {/* Railway → Docker */}
      <path d="M 700 622 L 760 622" stroke="rgba(148,163,184,0.3)" strokeWidth="1.5" fill="none" strokeDasharray="5 3" markerEnd="url(#arr)"/>

      {/* ══ NODE: Client  rect(60,80,280,168) ══
           tag   y=104  (80+24)
           title y=130  (80+50)
           d1    y=156  (80+76)
           d2    y=174  (80+94)
           d3    y=192  (80+112)
           d4    y=210  (80+130)
           bottom=210+16=226  → h=146 → use 168 for breathing room
      */}
      <g className="arch-node" filter="url(#gl-ind)">
        <rect x="60" y="80" width="280" height="168" rx="12" fill="url(#g-client)" stroke="rgba(99,102,241,0.35)" strokeWidth="1"/>
        <rect x="60" y="80" width="280" height="1" fill="rgba(165,180,252,0.28)"/>
        <text x="76" y="104" fill="rgba(129,140,248,0.75)" fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">FRONTEND</text>
        <text x="76" y="130" fill="#c7d2fe" fontSize="19" fontFamily="'Syne',sans-serif" fontWeight="800">Client Layer</text>
        <text x="76" y="156" fill="rgba(199,210,254,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">React 18 + TypeScript</text>
        <text x="76" y="174" fill="rgba(199,210,254,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Tailwind CSS + Vite</text>
        <text x="76" y="192" fill="rgba(199,210,254,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">70+ Components</text>
        <text x="76" y="210" fill="rgba(199,210,254,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">15,000+ LOC</text>
      </g>

      {/* ══ NODE: Server  rect(470,60,310,240) ══
           tag   y=84   (60+24)
           title y=110  (60+50)
           d1    y=136  (60+76)
           d2    y=154  (60+94)
           d3    y=172  (60+112)
           d4    y=190  (60+130)
           d5    y=208  (60+148)
           bottom=208+16=224 → h=240 (extra bottom pad is fine for largest node)
      */}
      <g className="arch-node" filter="url(#gl-grn)">
        <rect x="470" y="60" width="310" height="176" rx="12" fill="url(#g-server)" stroke="rgba(16,185,129,0.35)" strokeWidth="1"/>
        <rect x="470" y="60" width="310" height="1" fill="rgba(52,211,153,0.28)"/>
        <text x="486" y="84"  fill="rgba(52,211,153,0.75)"  fontSize="9"  fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">BACKEND · CORE</text>
        <text x="486" y="110" fill="#a7f3d0" fontSize="19" fontFamily="'Syne',sans-serif" fontWeight="800">Server Layer</text>
        <text x="486" y="136" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Node.js + Express</text>
        <text x="486" y="154" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">30+ API Endpoints</text>
        <text x="486" y="172" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Business Logic</text>
        <text x="486" y="190" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Feature Flags</text>
        <text x="486" y="208" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Rate Limiting · Error Handling</text>
      </g>

      {/* ══ NODE: MongoDB  rect(110,380,220,164) ══
           tag   y=404  (380+24)
           title y=430  (380+50)
           d1    y=456  (380+76)
           d2    y=474  (380+94)
           d3    y=492  (380+112)
           badge y=506  (492+14), h=18 → bottom=524+16=540 → h=160
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="110" y="380" width="220" height="160" rx="12" fill="url(#g-db)" stroke="rgba(239,68,68,0.35)" strokeWidth="1"/>
        <rect x="110" y="380" width="220" height="1" fill="rgba(248,113,113,0.28)"/>
        <text x="126" y="404" fill="rgba(248,113,113,0.75)" fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">DATABASE · ATLAS</text>
        <text x="126" y="430" fill="#fecaca" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">MongoDB</text>
        <text x="126" y="456" fill="rgba(254,202,202,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Mongoose ODM</text>
        <text x="126" y="474" fill="rgba(254,202,202,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Users · Submissions</text>
        <text x="126" y="492" fill="rgba(254,202,202,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Analytics</text>
        <rect x="126" y="506" width="90" height="18" rx="4" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
        <text x="171" y="519" textAnchor="middle" fill="rgba(254,202,202,0.65)" fontSize="9" fontFamily="'JetBrains Mono',monospace">via Mongoose</text>
      </g>

      {/* ══ NODE: Firebase  rect(460,380,170,118) ══
           tag   y=404  (380+24)
           title y=430  (380+50)
           d1    y=456  (380+76)
           d2    y=474  (380+94)
           bottom=474+16=490 → h=110 → use 118
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="400" y="380" width="170" height="118" rx="12" fill="url(#g-firebase)" stroke="rgba(251,146,60,0.35)" strokeWidth="1"/>
        <rect x="400" y="380" width="170" height="1" fill="rgba(251,146,60,0.28)"/>
        <text x="416" y="404" fill="rgba(251,146,60,0.75)" fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">AUTH · GOOGLE</text>
        <text x="416" y="430" fill="#fed7aa" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">Firebase</text>
        <text x="416" y="456" fill="rgba(254,215,170,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Authentication</text>
        <text x="416" y="474" fill="rgba(254,215,170,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">User Auth</text>
      </g>

      {/* ══ NODE: Gemini  rect(650,380,185,148) ══
           tag   y=404  (380+24)
           title y=430  (380+50)
           d1    y=456  (380+76)
           d2    y=474  (380+94)
           badge y=488  (474+14), h=18 → bottom=506+16=522 → h=142 → use 148
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="650" y="380" width="220" height="148" rx="12" fill="url(#g-gemini)" stroke="rgba(6,182,212,0.35)" strokeWidth="1"/>
        <rect x="650" y="380" width="220" height="1" fill="rgba(34,211,238,0.28)"/>
        <text x="666" y="404" fill="rgba(34,211,238,0.75)" fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">AI · GOOGLE</text>
        <text x="666" y="430" fill="#a5f3fc" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">Gemini API</text>
        <text x="666" y="456" fill="rgba(165,243,252,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">AI Evaluation</text>
        <text x="666" y="474" fill="rgba(165,243,252,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Statistical Analysis</text>
        <rect x="666" y="488" width="64" height="18" rx="4" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
        <text x="698" y="501" textAnchor="middle" fill="rgba(165,243,252,0.65)" fontSize="9" fontFamily="'JetBrains Mono',monospace">AI Calls</text>
      </g>

      {/* ══ NODE: Resend  rect(855,380,180,130) ══
           tag   y=404  (380+24)
           title y=430  (380+50)
           d1    y=456  (380+76)
           badge y=470  (456+14), h=18 → bottom=488+16=504 → h=124 → use 130
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="890" y="380" width="180" height="130" rx="12" fill="url(#g-resend)" stroke="rgba(52,211,153,0.3)" strokeWidth="1"/>
        <rect x="890" y="380" width="180" height="1" fill="rgba(52,211,153,0.25)"/>
        <text x="906" y="404" fill="rgba(52,211,153,0.65)" fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">EMAIL · SAAS</text>
        <text x="906" y="430" fill="#a7f3d0" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">Resend</text>
        <text x="906" y="456" fill="rgba(167,243,208,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Email Service</text>
        <rect x="906" y="470" width="90" height="18" rx="4" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
        <text x="951" y="483" textAnchor="middle" fill="rgba(167,243,208,0.65)" fontSize="9" fontFamily="'JetBrains Mono',monospace">98% Delivery</text>
      </g>

      {/* ══ NODE: Railway  rect(480,560,210,130) ══
           tag   y=584  (560+24)
           title y=610  (560+50)
           d1    y=636  (560+76)
           badge y=650  (636+14), h=18 → bottom=668+16=684 → h=124 → use 130
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="480" y="560" width="210" height="130" rx="12" fill="url(#g-railway)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
        <rect x="480" y="560" width="210" height="1" fill="rgba(255,255,255,0.15)"/>
        <text x="496" y="584" fill="rgba(203,213,225,0.6)"  fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">DEPLOYMENT</text>
        <text x="496" y="610" fill="#f1f5f9" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">Railway</text>
        <text x="496" y="636" fill="rgba(241,245,249,0.45)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Deployment Platform</text>
        <rect x="496" y="650" width="90" height="18" rx="4" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
        <text x="541" y="663" textAnchor="middle" fill="rgba(241,245,249,0.55)" fontSize="9" fontFamily="'JetBrains Mono',monospace">99.9% Uptime</text>
      </g>

      {/* ══ NODE: Docker  rect(760,560,185,112) ══
           tag   y=584  (560+24)
           title y=610  (560+50)
           d1    y=636  (560+76)
           d2    y=654  (560+94)
           bottom=654+16=670 → h=110 → use 112
      */}
      <g className="arch-node" filter="url(#gl-sm)">
        <rect x="760" y="560" width="185" height="112" rx="12" fill="url(#g-docker)" stroke="rgba(14,165,233,0.35)" strokeWidth="1"/>
        <rect x="760" y="560" width="185" height="1" fill="rgba(56,189,248,0.28)"/>
        <text x="776" y="584" fill="rgba(56,189,248,0.75)"  fontSize="9" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".18em">CONTAINER</text>
        <text x="776" y="610" fill="#bae6fd" fontSize="18" fontFamily="'Syne',sans-serif" fontWeight="800">Docker</text>
        <text x="776" y="636" fill="rgba(186,230,253,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Containerization</text>
        <text x="776" y="654" fill="rgba(186,230,253,0.55)" fontSize="12" fontFamily="'JetBrains Mono',monospace">Dev / Prod Sync</text>
      </g>

      {/* ── Header ── */}
      <circle cx="32" cy="32" r="5" fill="#6366f1" opacity=".9"/>
      <text x="46" y="37" fill="rgba(100,116,139,0.7)" fontSize="11" fontFamily="'JetBrains Mono',monospace" fontWeight="600" letterSpacing=".2em">SYSTEM ARCHITECTURE</text>
      <line x1="310" y1="32" x2="1070" y2="32" stroke="rgba(99,102,241,0.18)" strokeWidth="1"/>
    </svg>
  </div>
);

export default ArchitectureDiagram;
